import React, { useState } from 'react';
import { Zap, Settings, BarChart3 } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { FreeTierLock } from '../ui/FreeTierLock';
import { Modal } from '../ui/Modal';
import { WorkflowGenerationForm } from './WorkflowGenerationForm';
import { WorkflowBuilder } from './WorkflowBuilder';
import { WorkflowsList } from './WorkflowsList';
import { WorkflowRunsViewer } from './WorkflowRunsViewer';
import { apiClient } from '../../lib/api';
import { WorkflowDefinition } from '../../types/workflows';

interface WorkflowBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasetId: string;
  datasetName: string;
}

type ModalView = 'list' | 'generate' | 'edit' | 'runs';

export const WorkflowBuilderModal: React.FC<WorkflowBuilderModalProps> = ({
  isOpen,
  onClose,
  datasetId,
  datasetName
}) => {
  const [currentView, setCurrentView] = useState<ModalView>('list');
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isFreeTier } = useSubscription();

  const handleCreateNew = () => {
    setCurrentView('generate');
    setSelectedWorkflow(null);
  };

  const handleWorkflowGenerated = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setCurrentView('edit');
  };

  const handleEditWorkflow = async (workflow: WorkflowDefinition) => {
    try {
      setIsLoading(true);
      // Fetch complete workflow details including graph
      const fullWorkflow = await apiClient.getWorkflow(workflow.id!);
      setSelectedWorkflow(fullWorkflow);
      setCurrentView('edit');
    } catch (error) {
      console.error('Error fetching workflow details:', error);
      // Fallback to the workflow object passed from the list (without graph)
      setSelectedWorkflow(workflow);
      setCurrentView('edit');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewRuns = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setCurrentView('runs');
  };

  const handleBack = () => {
    if (currentView === 'generate' || currentView === 'edit' || currentView === 'runs') {
      setCurrentView('list');
      setSelectedWorkflow(null);
    }
  };

  const VIEW_CONFIG = {
    generate: {
      icon: Zap,
      title: 'Generate Workflow'
    },
    edit: {
      icon: Settings,
      title: selectedWorkflow?.name || 'Edit Workflow'
    },
    runs: {
      icon: BarChart3,
      title: `${selectedWorkflow?.name} - Runs`
    },
    list: {
      icon: Zap,
      title: 'Workflow Automation'
    }
  };

  const getTitleWithIcon = (title: string, IconComponent: any) => (
    <div className="flex items-center space-x-2">
      <IconComponent className="w-5 h-5 text-blue-600" />
      <span>{title}</span>
    </div>
  );

  const getTitle = () => {
    const config = VIEW_CONFIG[currentView as keyof typeof VIEW_CONFIG];
    const titleWithIcon = getTitleWithIcon(config.title, config.icon);

    if (currentView !== 'list') {
      return (
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBack}
            className="text-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-1 rounded transition-colors"
            title="Back to workflow list"
          >
            ←
          </button>
          {titleWithIcon}
        </div>
      );
    }

    return titleWithIcon;
  };

  const CONTENT_RENDERERS = {
    generate: () => (
      <WorkflowGenerationForm
        datasetId={datasetId}
        datasetName={datasetName}
        onWorkflowGenerated={handleWorkflowGenerated}
        onCancel={handleBack}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    ),
    edit: () => selectedWorkflow ? (
      <WorkflowBuilder
        workflow={selectedWorkflow}
        datasetId={datasetId}
        onSave={(workflow) => {
          setSelectedWorkflow(workflow);
        }}
        onCancel={handleBack}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    ) : null,
    runs: () => selectedWorkflow ? (
      <WorkflowRunsViewer
        workflow={selectedWorkflow}
      />
    ) : null,
    list: () => (
      <WorkflowsList
        datasetId={datasetId}
        onCreateNew={handleCreateNew}
        onEditWorkflow={handleEditWorkflow}
        onViewRuns={handleViewRuns}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    )
  };

  const renderContent = () => {
    const renderer = CONTENT_RENDERERS[currentView as keyof typeof CONTENT_RENDERERS] || CONTENT_RENDERERS.list;
    return renderer();
  };

  if (isFreeTier) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={getTitle()}
        size="full"
      >
        <div className="flex flex-col relative w-full h-[80vh] justify-center items-center">
          <FreeTierLock
            title="Upgrade to unlock Workflows"
            description="Subscribe to a paid plan to create automated workflows for your datasets."
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={getTitle()}
      size="full"
    >
      <div className="flex flex-col" style={{ minHeight: '600px', maxHeight: '85vh', height: '80vh' }}>
        {/* Subtitle */}
        <div className="mb-6 flex-shrink-0">
          <p className="text-sm text-gray-600">
            {currentView === 'generate' && `Create automation for ${datasetName}`}
            {currentView === 'edit' && 'Configure your workflow automation'}
            {currentView === 'runs' && 'View execution history and analytics'}
            {currentView === 'list' && `Manage automations for ${datasetName}`}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden" style={{ minHeight: '500px' }}>
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
};